/**
 * SavedSearchesPage - Manage saved property searches
 */
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { 
  Heart, Bell, Mail, Trash2, Edit2, MapPin, 
  Home, Bed, ArrowLeft, Loader2, Search,
  Clock, CheckCircle2, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { useCountry } from '../context/CountryContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SavedSearchesPage() {
  const { country } = useCountry();
  const [searches, setSearches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [emailInput, setEmailInput] = useState('');
  const [editingSearch, setEditingSearch] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const currency = country === 'UK' ? '£' : 'Rs.';
  
  // Load saved email from localStorage
  useEffect(() => {
    const savedEmail = localStorage.getItem('savedSearchEmail');
    if (savedEmail) {
      setEmail(savedEmail);
      setEmailInput(savedEmail);
    }
  }, []);
  
  // Fetch saved searches when email is set
  useEffect(() => {
    if (email) {
      fetchSavedSearches();
    } else {
      setLoading(false);
    }
  }, [email]);
  
  const fetchSavedSearches = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/property-saved-searches/user/${encodeURIComponent(email)}`);
      
      if (response.ok) {
        const data = await response.json();
        setSearches(data.searches || []);
      } else {
        toast.error('Failed to load saved searches');
      }
    } catch (error) {
      console.error('Error fetching saved searches:', error);
      toast.error('Failed to load saved searches');
    } finally {
      setLoading(false);
    }
  };
  
  const handleEmailSubmit = (e) => {
    e.preventDefault();
    if (emailInput.trim()) {
      localStorage.setItem('savedSearchEmail', emailInput.trim());
      setEmail(emailInput.trim());
    }
  };
  
  const handleDelete = async (searchId) => {
    try {
      const response = await fetch(`${API_URL}/api/property-saved-searches/${searchId}`, {
        method: 'DELETE'
      });
      
      if (response.ok) {
        setSearches(searches.filter(s => s.id !== searchId));
        toast.success('Search deleted');
      } else {
        toast.error('Failed to delete search');
      }
    } catch (error) {
      console.error('Error deleting search:', error);
      toast.error('Failed to delete search');
    }
    setDeleteConfirm(null);
  };
  
  const handleUpdate = async (searchId, updates) => {
    try {
      setSaving(true);
      const response = await fetch(`${API_URL}/api/property-saved-searches/${searchId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
      });
      
      if (response.ok) {
        setSearches(searches.map(s => 
          s.id === searchId ? { ...s, ...updates } : s
        ));
        toast.success('Settings updated');
        setEditingSearch(null);
      } else {
        toast.error('Failed to update settings');
      }
    } catch (error) {
      console.error('Error updating search:', error);
      toast.error('Failed to update settings');
    } finally {
      setSaving(false);
    }
  };
  
  const formatFilters = (filters) => {
    const parts = [];
    
    if (filters.location) {
      parts.push(filters.location);
    }
    
    if (filters.minPrice || filters.maxPrice) {
      const min = filters.minPrice ? `${currency}${filters.minPrice.toLocaleString()}` : 'Any';
      const max = filters.maxPrice ? `${currency}${filters.maxPrice.toLocaleString()}` : 'Any';
      parts.push(`${min} - ${max}`);
    }
    
    if (filters.bedrooms && filters.bedrooms !== 'any') {
      parts.push(filters.bedrooms === 'studio' ? 'Studio' : `${filters.bedrooms} bed`);
    }
    
    if (filters.propertyType && filters.propertyType !== 'any') {
      parts.push(filters.propertyType);
    }
    
    return parts.length > 0 ? parts.join(' • ') : 'No filters';
  };
  
  // Email entry screen
  if (!email) {
    return (
      <div className="min-h-screen bg-gray-50 py-12 px-4">
        <div className="max-w-md mx-auto">
          <Card className="p-8">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Heart className="w-8 h-8 text-blue-600" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900">My Saved Searches</h1>
              <p className="text-gray-500 mt-2">Enter your email to view and manage your saved property searches</p>
            </div>
            
            <form onSubmit={handleEmailSubmit} className="space-y-4">
              <div>
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  value={emailInput}
                  onChange={(e) => setEmailInput(e.target.value)}
                  placeholder="your@email.com"
                  className="mt-1"
                  data-testid="saved-searches-email-input"
                />
              </div>
              
              <Button type="submit" className="w-full" data-testid="view-searches-btn">
                View My Searches
              </Button>
            </form>
            
            <div className="mt-6 text-center">
              <Link to="/category/property" className="text-blue-600 hover:underline text-sm">
                ← Back to Property Search
              </Link>
            </div>
          </Card>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/category/property" className="text-gray-500 hover:text-gray-700">
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">My Saved Searches</h1>
                <p className="text-gray-500 text-sm">{email}</p>
              </div>
            </div>
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                localStorage.removeItem('savedSearchEmail');
                setEmail('');
                setEmailInput('');
              }}
            >
              Change Email
            </Button>
          </div>
        </div>
      </div>
      
      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        ) : searches.length === 0 ? (
          <Card className="p-12 text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Search className="w-8 h-8 text-gray-400" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">No Saved Searches</h2>
            <p className="text-gray-500 mb-6">
              Save a property search to get notified when new listings match your criteria.
            </p>
            <Link to="/category/property">
              <Button>
                <Home className="w-4 h-4 mr-2" />
                Browse Properties
              </Button>
            </Link>
          </Card>
        ) : (
          <div className="space-y-4">
            {searches.map(search => (
              <Card key={search.id} className="p-6" data-testid={`saved-search-${search.id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="font-semibold text-lg text-gray-900">{search.name}</h3>
                      {search.new_matches > 0 && (
                        <Badge className="bg-green-500">
                          {search.new_matches} new
                        </Badge>
                      )}
                    </div>
                    
                    <p className="text-gray-600 text-sm mb-3">
                      {formatFilters(search.filters)}
                    </p>
                    
                    {/* Notification Settings */}
                    <div className="flex items-center gap-4 text-sm">
                      <div className="flex items-center gap-1.5">
                        {search.notify_email ? (
                          <CheckCircle2 className="w-4 h-4 text-green-500" />
                        ) : (
                          <AlertCircle className="w-4 h-4 text-gray-400" />
                        )}
                        <span className={search.notify_email ? 'text-green-700' : 'text-gray-500'}>
                          Email {search.notify_email ? 'On' : 'Off'}
                        </span>
                      </div>
                      
                      <div className="flex items-center gap-1.5">
                        <Clock className="w-4 h-4 text-gray-400" />
                        <span className="text-gray-600 capitalize">
                          {search.notify_frequency || 'Instant'}
                        </span>
                      </div>
                      
                      {search.total_matches > 0 && (
                        <span className="text-gray-500">
                          {search.total_matches} total matches
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setEditingSearch(search)}
                      data-testid={`edit-search-${search.id}`}
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-red-600 hover:bg-red-50"
                      onClick={() => setDeleteConfirm(search)}
                      data-testid={`delete-search-${search.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                    
                    <Link to={`/category/property?${new URLSearchParams(search.filters).toString()}`}>
                      <Button size="sm">
                        View Results
                      </Button>
                    </Link>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
      
      {/* Edit Dialog */}
      <Dialog open={!!editingSearch} onOpenChange={() => setEditingSearch(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Notification Settings</DialogTitle>
            <DialogDescription>
              Choose how you want to be notified about new matches for "{editingSearch?.name}"
            </DialogDescription>
          </DialogHeader>
          
          {editingSearch && (
            <div className="space-y-6 py-4">
              {/* Name */}
              <div className="space-y-2">
                <Label htmlFor="edit-name">Search Name</Label>
                <Input
                  id="edit-name"
                  value={editingSearch.name}
                  onChange={(e) => setEditingSearch({ ...editingSearch, name: e.target.value })}
                />
              </div>
              
              {/* Email Notifications */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-gray-500" />
                  <div>
                    <Label>Email Notifications</Label>
                    <p className="text-sm text-gray-500">Get emails when new properties match</p>
                  </div>
                </div>
                <Switch
                  checked={editingSearch.notify_email}
                  onCheckedChange={(checked) => setEditingSearch({ ...editingSearch, notify_email: checked })}
                />
              </div>
              
              {/* Push Notifications */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Bell className="w-5 h-5 text-gray-500" />
                  <div>
                    <Label>Push Notifications</Label>
                    <p className="text-sm text-gray-500">Get browser push notifications</p>
                  </div>
                </div>
                <Switch
                  checked={editingSearch.notify_push}
                  onCheckedChange={(checked) => setEditingSearch({ ...editingSearch, notify_push: checked })}
                />
              </div>
              
              {/* Frequency */}
              <div className="space-y-2">
                <Label>Notification Frequency</Label>
                <Select 
                  value={editingSearch.notify_frequency || 'instant'}
                  onValueChange={(value) => setEditingSearch({ ...editingSearch, notify_frequency: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="instant">Instant - As soon as new listings are posted</SelectItem>
                    <SelectItem value="daily">Daily Digest - One email per day</SelectItem>
                    <SelectItem value="both">Both - Instant + Daily summary</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingSearch(null)}>
              Cancel
            </Button>
            <Button 
              onClick={() => handleUpdate(editingSearch.id, {
                name: editingSearch.name,
                notify_email: editingSearch.notify_email,
                notify_push: editingSearch.notify_push,
                notify_frequency: editingSearch.notify_frequency
              })}
              disabled={saving}
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Saved Search?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete "{deleteConfirm?.name}" and you will no longer receive notifications for this search.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={() => handleDelete(deleteConfirm.id)}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
